import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crown, TrendingUp, Clock, Gavel, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useLang } from "@/LangContext";
import Countdown from "@/components/Countdown";
import { authAxios, apiError, fmtUSD } from "@/lib/auth";

function Pending({ bidder }) {
  const { t } = useLang();
  const p = t.portal;
  const rejected = bidder.status === "rejected";
  return (
    <div data-testid={`portal-status-${bidder.status}`} className="border border-white/10 bg-[#0E1119] p-10 text-center">
      {rejected ? <XCircle size={40} className="mx-auto text-red-400" /> : <Clock size={40} className="mx-auto text-amber-400" />}
      <h2 className="mt-6 font-display text-3xl font-bold uppercase text-white">{rejected ? p.rejectedTitle : p.pendingTitle}</h2>
      <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">{rejected ? p.rejectedDesc : p.pendingDesc}</p>
    </div>
  );
}

export default function BidderDashboard({ bidder }) {
  const { t } = useLang();
  const p = t.portal;
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const { data } = useQuery({
    queryKey: ["auction-private"],
    queryFn: async () => (await authAxios.get("/auction/private")).data,
    refetchInterval: 10_000,
    enabled: bidder.status === "approved",
  });

  if (bidder.status !== "approved") return <Pending bidder={bidder} />;
  if (!data) return <Loader2 className="mx-auto mt-20 animate-spin text-amber-400" />;

  const publicName = bidder.display_mode === "name" ? bidder.company || bidder.name : bidder.alias;
  const mine = data.history.filter((h) => h.mine);
  const position = data.i_lead ? "leading" : mine.length ? "outbid" : "notBid";

  const placeBid = async (e) => {
    e.preventDefault();
    const v = parseFloat(amount);
    if (!Number.isFinite(v) || v < data.min_next_bid) return toast.error(`${p.minNext}: ${fmtUSD(data.min_next_bid)}`);
    if (!window.confirm(p.confirm.replace("{amount}", fmtUSD(v)))) return;
    setLoading(true);
    try {
      const { data: r } = await authAxios.post("/auction/bid", { amount: v });
      toast.success(p.bidOk);
      if (r.extended) toast.info(p.extended);
      setAmount("");
      qc.invalidateQueries({ queryKey: ["auction-private"] });
      qc.invalidateQueries({ queryKey: ["auction-public"] });
    } catch (err) {
      toast.error(apiError(err, "Error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="bidder-dashboard" className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
      <div className="space-y-6">
        <div className="border border-white/10 bg-[#0E1119] p-7">
          <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.welcome}</span>
          <h2 className="mt-2 font-display text-2xl font-bold uppercase text-white">{bidder.name}</h2>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 border border-amber-400/40 px-3 py-1 font-code text-[10px] uppercase tracking-[0.2em] text-amber-400">
              <CheckCircle2 size={11} /> {p.statusApproved}
            </span>
            <span data-testid="bidder-public-name" className="font-code text-xs text-slate-400">
              {p.yourAlias}: <span className="text-white">{publicName}</span>
            </span>
          </div>
        </div>

        <div className="border border-white/10 bg-[#0C0F16] p-7">
          <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.closesIn}</span>
          <div className="mt-3">
            <Countdown endsAt={data.ends_at} compact />
          </div>
        </div>

        <div
          data-testid={`bidder-position-${position}`}
          className={`border p-7 ${position === "leading" ? "border-amber-400/60 bg-amber-400/[0.06]" : "border-white/10 bg-[#0E1119]"}`}
        >
          <div className="flex items-center gap-3">
            <Crown size={18} className={position === "leading" ? "text-amber-400" : "text-slate-500"} />
            <span className="font-display text-lg font-bold uppercase text-white">{p[position]}</span>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="relative overflow-hidden border border-amber-400/50 bg-[#0E1119] p-7 sm:p-9">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="grid grid-cols-2 gap-6">
            <div>
              <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.highBid}</span>
              <div data-testid="dashboard-high-bid" className="mt-2 font-display text-3xl font-bold text-amber-400 sm:text-4xl">
                {data.high_bid != null ? fmtUSD(data.high_bid) : "—"}
              </div>
              <div className="mt-1 text-xs text-slate-500">{data.leader || p.noHigh}</div>
            </div>
            <div>
              <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.minNext}</span>
              <div data-testid="dashboard-min-next" className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">
                {fmtUSD(data.min_next_bid)}
              </div>
            </div>
          </div>

          {data.open ? (
            <form onSubmit={placeBid} className="mt-8 border-t border-white/10 pt-6">
              <label className="block">
                <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{p.bidLabel}</span>
                <div className="relative mt-2">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input
                    data-testid="bid-amount-input"
                    type="number"
                    min={data.min_next_bid}
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={String(data.min_next_bid)}
                    className="w-full border border-white/15 bg-[#0A0C10] py-4 pl-8 pr-4 font-display text-2xl font-bold text-white outline-none focus:border-amber-400"
                  />
                </div>
              </label>
              <button
                type="submit"
                data-testid="place-bid-button"
                disabled={loading}
                className="mt-4 flex w-full items-center justify-center gap-2 bg-amber-400 px-5 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Gavel size={16} />}
                {p.placeBid}
              </button>
            </form>
          ) : (
            <p data-testid="auction-closed-msg" className="mt-8 border-t border-white/10 pt-6 text-sm text-slate-400">{p.closed}</p>
          )}
        </div>

        <div className="border border-white/10 bg-[#0C0F16] p-7">
          <div className="flex items-center gap-3 text-slate-300">
            <TrendingUp size={16} />
            <span className="font-code text-[11px] uppercase tracking-[0.3em]">{p.history}</span>
          </div>
          <ul data-testid="bid-history" className="mt-4 divide-y divide-white/5">
            {data.history.length === 0 && <li className="py-3 text-sm text-slate-500">{p.noHigh}</li>}
            {data.history.map((h, i) => (
              <li key={h.at + i} data-testid={`bid-history-item-${i}`} className="flex items-center justify-between py-3 text-sm">
                <span className={h.mine ? "font-semibold text-amber-400" : "text-slate-300"}>
                  {h.mine ? `${p.mine} · ` : ""}{h.display}
                </span>
                <span className="font-display font-bold text-white">{fmtUSD(h.amount)}</span>
                <span className="font-code text-[10px] text-slate-500">{new Date(h.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
